import React from 'react';

import cx from 'classnames';

import { Action } from '~shared/components/ActionMenu/ActionMenu';
import { Badge } from '~shared/components/Badge';
import { Divider } from '~shared/components/Divider';
import { proCheck } from '~teacher/util/proCheck';

type Props<T> = Action<T> & { data: Array<T>; closePopper: () => void };

export function ActionMenuItem<T>({
  disabled,
  searchKey,
  className,
  color,
  action,
  data,
  title,
  description,
  divider,
  closePopper,
  paywallCallback,
  showProBadge,
}: Props<T>) {
  const isDisabled = disabled?.({ data }) ?? false;
  const dataKey = isDisabled ? undefined : searchKey;
  const isPro = proCheck.isPro();
  const componentClassName = cx(
    'flex w-full px-4 py-2 whitespace-no-wrap font-sans text-base leading-8 text-left border-0 rounded-1 bg-white text-blue-900 outline-none hocus:bg-cyan-50',
    { ['cursor-default']: isDisabled, ['cursor-pointer']: !isDisabled },
    { ['opacity-50']: isDisabled, ['opacity-100']: !isDisabled },
    className,
    color
  );
  const onClick = () => {
    if (paywallCallback && !isPro) {
      paywallCallback();
      return;
    }
    if (isDisabled) return;
    return action({ data, close: closePopper });
  };
  // here is where you apply the disabled items
  return (
    <>
      {divider ? (
        <div tabIndex={-1} className={'w-full py-1'}>
          <Divider />
        </div>
      ) : null}
      <div className="px-2 py-1">
        <div
          // tabIndex={disabled ? (disabled({ data }) ? -1 : 0) : 0} <- will prevent disabled items from being tabbed
          tabIndex={0}
          // prevent disabled items from being searchable via keyboard
          data-key={dataKey}
          // ensure that a focus state exists on disabled items, so the user knows the location of focus
          className={componentClassName}
          onClick={onClick}
        >
          <div>
            <div
              className={cx('text-base font-sans', color ?? 'text-blue-900')}
              style={{ color: color ?? '' }}
            >
              <div className="flex">
                {title}
                {showProBadge && !isPro ? (
                  <Badge className="ml-2" tone="pro">
                    PRO
                  </Badge>
                ) : null}
              </div>
            </div>
            {description !== undefined && description !== null ? (
              <p className="text-grey-800 text-xs whitespace-normal mt-2 font-sans ">
                {description}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}
